import React from "react";
import { useParams } from "react-router-dom";

import { Text } from "@performyard/py-components";

import Breadcrumbs from "../../../components/Breadcrumbs";
import { Divider } from "../../../components/Divider";
import DetailsOptionsDropdown from "./DetailsOptionsDropdown";
import ReviewCycleReviewsDetailsTable from "./Table";

import { useReviewCycleDetails } from "../../../api/hooks";
import useScrollToTopOfPageOnMount from "../../../hooks/useScrollToTopOfPageOnMount";
import {
	ReviewCycleBuilderPageProvider,
	useReviewCycleBuilderPageContext,
} from "./hooks/ReviewCycleBuilderPageContext";

import { currentUserQuery, reviewCycleDetailsQuery, reviewCycleReviewsDetailsStreamQuery } from "../../../api/queries";
import { dataLoader } from "../../../api/queries/dataLoader";
import { SimplifiedQuery } from "../../../api/queries/dataLoader";

import BasePageTemplate from "../../../pageTemplates/BasePageTemplate";
import FormCompletionCard from "./Cards/FormCompletionCard";
import ReviewAutomationCard from "./Cards/ReviewAutomationCard";
import ReviewCompletionCard from "./Cards/ReviewCompletionCard";
import ReviewCycleInformationCard from "./Cards/ReviewCycleInformationCard";

export const reviewCycleDetailsLoader = ({ params }) => {
	const mainQuery: (() => SimplifiedQuery)[] = [
		() => reviewCycleDetailsQuery(params.cycleId!),
		() => reviewCycleReviewsDetailsStreamQuery(params.cycleId!),
	];

	return dataLoader([[currentUserQuery], mainQuery]);
};

const ReviewCycleDetailsContent = () => {
	useScrollToTopOfPageOnMount();
	const { cycleId: reviewCycleId = "" } = useParams();
	const { renderPage } = useReviewCycleBuilderPageContext();

	const { data: reviewCycleDetails } = useReviewCycleDetails(reviewCycleId);

	const reviewCycleName = reviewCycleDetails?.name;

	const hasAutomation = !!reviewCycleDetails?.autoflow;
	const cardWidth = hasAutomation ? "normal" : "wide";

	if (renderPage) {
		return renderPage;
	}

	return (
		<BasePageTemplate title="Review Cycle Details">
			<div className="mt-2">
				<Breadcrumbs
					pages={[
						{
							pageID: "review-cycles",
							label: "Review Cycles",
							url: "/company/reviews/cycles",
						},
						{
							pageID: "review-cycle-details",
							label: reviewCycleName,
							url: `/review-cycle/${reviewCycleId}`,
						},
					]}
				/>
			</div>
			<div className="mt-4 flex justify-between">
				<Text size="heading-5" weight="semi-bold">
					{reviewCycleName}
				</Text>
				<DetailsOptionsDropdown reviewCycleId={reviewCycleId} />
			</div>
			<div className="mt-6 flex flex-wrap items-center justify-center gap-2 2xl:justify-between">
				<ReviewCycleInformationCard width={cardWidth} />
				<ReviewAutomationCard />
				<ReviewCompletionCard width={cardWidth} />
				<FormCompletionCard width={cardWidth} />
			</div>

			<Divider className="my-6" />
			<ReviewCycleReviewsDetailsTable reviewCycleId={reviewCycleId} />
		</BasePageTemplate>
	);
};

const ReviewCycleDetails = () => {
	const { cycleId: reviewCycleId = "" } = useParams();

	return (
		<ReviewCycleBuilderPageProvider reviewCycleId={reviewCycleId}>
			<ReviewCycleDetailsContent />
		</ReviewCycleBuilderPageProvider>
	);
};

export default ReviewCycleDetails;
